import React, { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { InputLabel } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";

import {
  AlertComponent,
  DescriptionInputComponent,
  SelectInputComponent,
  TextInputComponent,
} from "../../../../../common/components";
import DatePickerComponent from "../../../../../common/components/date-picker/date-picker-compoenent";
import CloseIcon from "@mui/icons-material/Close";
import { ADJUSTMENT_TYPE_OPTIONS } from "../../order-management-helper";
import { addAdjustmentToInvoice } from "../../../../../api/order-management";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

interface CreateInvoicePopupProps {
  isDialogOpen: boolean;
  setIsOpen: (open: boolean) => void;
  handleAddClick?: (values: any) => void;
  invoiceId?: number;
  setAlert: (alert: {
    isOpen?: boolean;
    message?: any;
    type?: string;
  }) => void;
}

const validationSchema = Yup.object().shape({
  adjustmentType: Yup.string().required("Adjustment type is required"),
  totalAmount: Yup.number().required("Amount is required"),
  notes: Yup.string(),
  adjustmentDate: Yup.date().nullable(),
});

const AddAdjustmentPopup: React.FC<CreateInvoicePopupProps> = ({
  isDialogOpen,
  setIsOpen,
  invoiceId,
  setAlert
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // true if screen ≤ 600px
  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleSave = async (values: any) => {
    setIsLoading(true);
    try {
      if (!invoiceId && !values) {
        return;
      }
      const adjustmentData = {
        invoiceId: invoiceId,
        adjustmentType: values.adjustmentType,
        amount: values.totalAmount,
        reason: values.notes,
        adjustmentDate: values.adjustmentDate
          ? dayjs(values.adjustmentDate).format("YYYY-MM-DD")
          : null,
      };
      const response = await addAdjustmentToInvoice(adjustmentData, invoiceId);
      setAlert({
        isOpen: true,
        message: response?.data?.data?.message || "Adjustment saved successfully",
        type: "success",
      });
      setTimeout(() => {
        setIsOpen(false);
      }, 1000);
    } catch (error: any) {
      console.error("Error saving adjustment:", error);
      setAlert({
        isOpen: true,
        message: error?.response?.data?.message || "Failed to save adjustment",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleCancel}
      aria-labelledby="create-invoice-title"
      className="update-popup-wrapper add-adjustment-popup"
      slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "570px", // 90% width on mobile
              maxWidth: "95vw",                  // prevent overflow
              minHeight: isMobile ? "auto" : "280px",
              borderBottomLeftRadius: '30px',
              borderBottomRightRadius: '30px',
              top: 0,  // add spacing from top on mobile
              margin: 0,
              position: "absolute",
            },
          },
        }}
    >
      <DialogTitle
        id="create-invoice-title"
        style={{
          borderBottom: "1px solid #ddd",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Add Adjustment
        <CloseIcon fontSize="small" onClick={handleCancel} />
      </DialogTitle>

      <Formik
        initialValues={{
          adjustmentType: "",
          totalAmount: "",
          notes: "",
          adjustmentDate: null,
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleSave(values);
        }}
      >
        {({ values, setFieldValue, touched, errors }) => (
          <Form>
            <DialogContent style={{ padding: "20px 24px" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <InputLabel style={{ minWidth: "140px", fontWeight: 500 }}>
                  Adjustment Type
                </InputLabel>
                <div style={{ flex: 1 }}>
                  <SelectInputComponent
                    fieldName="adjustmentType"
                    placeholder="Select Adjustment Type"
                    options={ADJUSTMENT_TYPE_OPTIONS}
                    value={values.adjustmentType}
                    onChange={(value: any) =>
                      setFieldValue("adjustmentType", value)
                    }
                    isShowError={!!touched.adjustmentType && !!errors.adjustmentType}
                    isRequired
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <InputLabel style={{ minWidth: "140px", fontWeight: 500 }}>
                  Amount
                </InputLabel>
                <div style={{ flex: 1 }}>
                  <TextInputComponent
                    type="text"
                    fieldName="totalAmount"
                    placeholder="Enter amount"
                    value={values.totalAmount}
                    onChange={(e: any) =>
                      setFieldValue("totalAmount", e?.target?.value)
                    }
                    isNumber={true}
                    minlength="3"
                    maxlength="50"
                    isRequired
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <InputLabel style={{ minWidth: "140px", fontWeight: 500 }}>
                  Reason/Notes
                </InputLabel>
                <div>
                  <DescriptionInputComponent
                    fieldName="notes"
                    placeholder="Enter reason/notes"
                    value={values.notes}
                    onChange={(e: any) =>
                      setFieldValue("notes", e.target.value)
                    }
                    rows={4}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <InputLabel style={{ minWidth: "140px", fontWeight: 500 }}>
                  Adjustment Date
                </InputLabel>
                <div style={{ flex: 1 }}>
                  <DatePickerComponent
                    fieldName="adjustmentDate"
                    label="Adjustment Date"
                    value={
                      values?.adjustmentDate ? dayjs(values.adjustmentDate) : null
                    }
                    onChange={(date) => setFieldValue("adjustmentDate", date)}
                    minDate={dayjs("1900-01-01")}
                    maxDate={dayjs()}
                    optional={true}
                    isLabel={false}
                  />
                </div>
              </div>
            </DialogContent>

            <DialogActions
              className="button-wrapper"
              style={{ padding: "16px 24px" }}
            >
              <Button onClick={handleCancel} className="cancel-btn">
                Cancel
              </Button>
              <Button
                type="submit"
                className="update-btn"
                style={{ width: "74px", letterSpacing: "-0.02em" }}
              >
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddAdjustmentPopup;
